import React, { useEffect, useState } from "react";
import { FaLock, FaUnlockAlt } from "react-icons/fa";
import { SortEndHandler } from "react-sortable-hoc";
import styled from "styled-components";
import EditableTitle from "../../../App/Common/Components/EditableTitle";
import { typeTitle } from "../../../formatters/courseAuthor";
import { fontSizeNumbers } from "../../../styles/fontSizes";
import { getSortableHelperContainer } from "../../../utils";
import { courseComponentViews, courseInstructionViews } from "../../../utils/courseViews";
import { AddInstructionPartWithDropZone } from "../../pages/CourseAuthor/CourseInstruction/Parts/AddInstructionPart";
import { PageLockSettings } from "../../pages/CourseAuthor/CourseInstruction/Parts/PageLockSettings";
import { H1 } from "../../utils/Headings";
import { CourseInstructionType, ICourseInstructionContentPart } from "../types";
import { SortableInstructionList } from "./SortableInstructionList";
import { PreviewInstructionProps } from "./types";
import { Warning, WarningHeader, WarningMessage } from "../../../App/Common/Styles/Alert";
import { CheckBox } from "../../pages/CourseAuthor/FormElements";
import { ItemLabel } from "../../../App/Common/Components/FormComponents";

interface IPseudoLessonComponent {
  togglePseudoLesson: (checked: boolean) => void;
  isPseudoLesson: boolean;
}

const EditableTitleWithLock = styled.div`
  justify-content: start;
  display: flex;
  width: 100%;
`;

export const PreviewInstructionComponent: React.FC<PreviewInstructionProps> = props => {
  const [displayLockSetting, setDisplayLockSetting] = useState<boolean>(false);
  const toggleLockSettings = () => setDisplayLockSetting(!displayLockSetting);
  const parts: ICourseInstructionContentPart[] = courseInstructionViews.content(props.instruction) || [];
  const visibleParts = parts.filter(part => !part.deleted);
  const pageLockSettings = courseInstructionViews.pageLockSettings(props.instruction);
  const getTitle = courseComponentViews.localizedTitle(props.currentLocale);
  const IconLock = pageLockSettings && pageLockSettings.isEnabled ? FaLock : FaUnlockAlt;
  const isPseudoLesson = props.instruction.content.pseudoLesson;
  const instructionType = props.instruction.content.type;
  const togglePseudoLesson = props.togglePseudoLesson;

  const onSortEnd: SortEndHandler = ({ newIndex: to, oldIndex: from }) => from !== to && props.sortPart({ from, to });

  useEffect(() => {
    setDisplayLockSetting(false);
  }, [props.instruction.id]);

  return (
    <>
      <H1>
        <EditableTitleWithLock>
          <span>
            <IconLock size={20} onClick={toggleLockSettings} />
          </span>
          <EditableTitle
            addTitle={props.addTitle}
            currentTitle={getTitle(props.instruction)}
            defaultTitle={typeTitle(props.instruction.content.type)}
            fontSize={fontSizeNumbers.h1}
            maxWidth={90}
            modifyTitle={props.modifyTitle}
            isMainTitle={true}
          />
        </EditableTitleWithLock>
      </H1>
      {props.isAuthor && renderPseudoLessonComponent(instructionType, togglePseudoLesson, isPseudoLesson)}
      {displayLockSetting && (
        <PageLockSettings settings={pageLockSettings} modifyPageLockSettings={props.modifyPageLockSettings} />
      )}
      <SortableInstructionList
        addPart={props.addPart}
        axis="y"
        helperContainer={getSortableHelperContainer()}
        courseId={props.courseId}
        currentLocale={props.currentLocale}
        editOperations={props.editOperations}
        instruction={props.instruction}
        lockAxis="y"
        onSortEnd={onSortEnd}
        partOperations={props.partOperations}
        parts={parts}
        unitPath={props.displayedComponentUnitPath}
        useDragHandle={true}
      />
      <AddInstructionPartWithDropZone
        dropPosition={undefined}
        dropZoneType="content"
        newInstructionType={null}
        editOperations={props.editOperations}
        path={props.displayedComponentUnitPath}
        instructionSlide={props.instruction}
        addPart={props.addPart}
        includeExplanation={true}
        includeGrids={true}
        displayOnStart={visibleParts.length === 0}
      />
    </>
  );
};

const renderPseudoLessonComponent = (
  instructionType: CourseInstructionType,
  togglePseudoLesson: (checked: boolean) => void,
  isPseudoLesson: boolean
) => {
  const isSummaryOrAppendix =
    instructionType === CourseInstructionType.Summary || instructionType === CourseInstructionType.Appendix;

  return (
    isSummaryOrAppendix && (
      <PseudoLessonComponent togglePseudoLesson={togglePseudoLesson} isPseudoLesson={isPseudoLesson} />
    )
  );
};

const PseudoLessonComponent: React.FC<IPseudoLessonComponent> = props => {
  const { togglePseudoLesson, isPseudoLesson } = props;

  const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { checked } = event.target;
    togglePseudoLesson(checked);
  };

  return (
    <Warning>
      <WarningHeader>Pseudo-lesson</WarningHeader>
      <ItemLabel>
        <CheckBox
          style={{ margin: "0 1rem 2rem 0" }}
          type="checkbox"
          data-testid="pseudo-lesson-checkbox"
          name="pseudo-lesson"
          checked={isPseudoLesson}
          onChange={handleCheckboxChange}
        />
        Enable pseudo-lesson
      </ItemLabel>
      <WarningMessage>
        Enabling this option will treat summary or appendix slides as lesson slides in that they need to be visited
        before a unit is considered complete, and that they need to be retaken when a learner fails the post-assessment.
      </WarningMessage>
    </Warning>
  );
};
